import React, { useState, useMemo } from 'react';
import { Search, Check, ChevronLeft, ChevronRight, Minus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './CompactMapSidebar.css';

export function CompactMapSidebar({
  sections,
  stations,
  selectedSections,
  selectAllSections,
  onSectionClick,
  onSelectAllChange,
  onSectionsChange,
  isOpen,
  onToggle,
  grouping,
  currentLevel,
  currentGroups,
  breadcrumbItems = [],
  onNavigateUp,
  onGroupDrill,
  onGroupToggle,
  onSectionToggle,
  selectionDisabled = false,
}) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const effectiveLevel = currentLevel || grouping?.groupingLevel || 'section';

  const resolvedGroups = useMemo(() => {
    if (Array.isArray(currentGroups) && currentGroups.length > 0) {
      return currentGroups;
    }
    return sections.map(section => ({
      ...section,
      areaName: section.name,
      sectionIds: section.id ? [section.id] : [],
    }));
  }, [currentGroups, sections]);

  const filteredGroups = useMemo(() => {
    if (!searchQuery) return resolvedGroups;
    const query = searchQuery.toLowerCase();
    return resolvedGroups.filter(group => (group.areaName || group.name || '')
      .toLowerCase()
      .includes(query));
  }, [resolvedGroups, searchQuery]);

  const handleSectionToggle = (sectionId) => {
    if (selectionDisabled) return;
    if (onSectionToggle) {
      onSectionToggle(sectionId);
      return;
    }
    if (selectAllSections) {
      // If all is selected, switch to specific selection
      const allSectionIds = sections.map(s => s.id);
      const newSections = allSectionIds.filter(id => id !== sectionId);
      onSectionsChange(newSections);
      onSelectAllChange(false);
    } else {
      const isSelected = selectedSections.includes(sectionId);
      if (isSelected) {
        onSectionsChange(selectedSections.filter(id => id !== sectionId));
      } else {
        onSectionsChange([...selectedSections, sectionId]);
      }
    }
  };

  const handleGroupToggle = (group) => {
    if (selectionDisabled) return;
    const sectionIds = Array.isArray(group.sectionIds)
      ? group.sectionIds.filter(Boolean)
      : (group.id ? [group.id] : []);
    if (sectionIds.length === 0) return;
    if (onGroupToggle) {
      onGroupToggle(sectionIds);
      return;
    }
    sectionIds.forEach((id) => handleSectionToggle(id));
  };

  const getGroupSelectionState = (group) => {
    if (selectAllSections) return 'all';
    const sectionIds = Array.isArray(group.sectionIds)
      ? group.sectionIds.filter(Boolean)
      : (group.id ? [group.id] : []);
    if (sectionIds.length === 0) return 'none';
    const selectedCount = sectionIds.filter(id => selectedSections.includes(id)).length;
    if (selectedCount === 0) return 'none';
    if (selectedCount === sectionIds.length) return 'all';
    return 'partial';
  };

  return (
    <>
      {/* Sidebar */}
      <div className={`compact-map-sidebar ${isOpen ? 'compact-map-sidebar--open' : ''}`}>
        {/* Header */}
        <div className="compact-map-sidebar__header">
          <h3 className="compact-map-sidebar__title">{t('analytics.sections.selectSidebarTitle')}</h3>
        </div>

        {(breadcrumbItems.length > 0 || (grouping && grouping.groupingLevel !== 'section' && currentLevel !== grouping.groupingLevel)) && (
          <div className="compact-map-sidebar__breadcrumb">
            {onNavigateUp && (
              <button
                type="button"
                className="compact-map-sidebar__breadcrumb-back"
                onClick={onNavigateUp}
              >
                <ChevronLeft size={14} />
                <span>{t('common.back', { defaultValue: 'Back' })}</span>
              </button>
            )}
            {breadcrumbItems.length > 0 && (
              <div className="compact-map-sidebar__breadcrumb-trail">
                {breadcrumbItems.map((item) => item.name).join(' / ')}
              </div>
            )}
          </div>
        )}

        {/* Search */}
        <div className="compact-map-sidebar__search">
          <Search size={16} className="compact-map-sidebar__search-icon" />
          <input
            type="text"
            placeholder={t('analytics.sections.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="compact-map-sidebar__search-input"
          />
        </div>
        
        
        {/* Section List */}
        <div className="compact-map-sidebar__list">
          {filteredGroups.length === 0 ? (
            <div className="compact-map-sidebar__empty">
              {t('analytics.sections.noneFound')}
            </div>
          ) : (
            filteredGroups.map((group) => {
              const selectionState = getGroupSelectionState(group);
              const isSelected = selectionState === 'all';
              const isPartial = selectionState === 'partial';
              const isDrillable = effectiveLevel !== 'section' && typeof onGroupDrill === 'function';

              return (
                <div
                  key={group.id || group.areaName}
                  className={`compact-map-sidebar__item ${
                    isSelected ? 'compact-map-sidebar__item--selected' : ''
                  } ${isPartial ? 'compact-map-sidebar__item--partial' : ''}`}
                >
                  <button
                    type="button"
                    className={`compact-map-sidebar__item-checkbox ${
                      isPartial ? 'compact-map-sidebar__item-checkbox--partial' : ''
                    }`}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleGroupToggle(group);
                    }}
                    aria-pressed={isSelected || isPartial}
                    aria-disabled={selectionDisabled}
                    disabled={selectionDisabled}
                  >
                    {isSelected && <Check size={12} />}
                    {isPartial && !isSelected && <Minus size={12} />}
                  </button>
                  <button
                    type="button"
                    className="compact-map-sidebar__item-main"
                    onClick={() => {
                      if (effectiveLevel === 'section') {
                        if (!selectionDisabled) {
                          handleSectionToggle(group.id);
                        }
                      } else if (isDrillable) {
                        onGroupDrill(group);
                      }
                    }}
                    disabled={selectionDisabled && effectiveLevel === 'section'}
                  >
                    <div className="compact-map-sidebar__item-content">
                      <div className="compact-map-sidebar__item-header">
                        <div className="compact-map-sidebar__item-name">{group.areaName || group.name}</div>
                      </div>
                    </div>
                    {isDrillable && <ChevronRight size={14} className="compact-map-sidebar__item-drill" />}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}

